import React, { useState, useEffect, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

interface ImageLightboxProps {
  images: string[];
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

export function ImageLightbox({ images, initialIndex, isOpen, onClose }: ImageLightboxProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true, 
    direction: 'rtl',
    duration: 20 
  });
  
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const [showControls, setShowControls] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);

  // חסימת החלקה שמאלה/ימינה של הקרוסלה כאשר התמונה בהגדלה
  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit({ watchDrag: !isZoomed });
    }
  }, [isZoomed, emblaApi]);

  // סנכרון אינדקס התחלתי ואיפוס זום במעבר תמונה
  useEffect(() => {
    if (isOpen && emblaApi) {
      emblaApi.scrollTo(initialIndex, true); // true = instant jump
      
      const onSelect = () => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
        setIsZoomed(false); // חזרה למצב רגיל כשמעבירים תמונה (דרך חצים או נקודות)
      };
      
      emblaApi.on('select', onSelect);
      return () => {
        emblaApi.off('select', onSelect);
      };
    }
  }, [isOpen, emblaApi, initialIndex]);

  // ניווט מקלדת (מושבת כאשר יש זום)
  useEffect(() => {
    if (!isOpen) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && !isZoomed) emblaApi?.scrollNext(); 
      if (e.key === 'ArrowLeft' && !isZoomed) emblaApi?.scrollPrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, emblaApi, isZoomed]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  // לוגיקה ל-Swipe Down לסגירה במובייל
  const [touchStart, setTouchStart] = useState<number | null>(null);
  
  const handleTouchStart = (e: React.TouchEvent) => {
    // נאפשר סגירה בהחלקה רק אם אין זום פעיל
    if (!isZoomed) {
      setTouchStart(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || isZoomed) return;
    const touchEnd = e.changedTouches[0].clientY;
    if (touchEnd - touchStart > 80) { // רגישות גלילה לסגירה
      onClose();
    }
    setTouchStart(null);
  };

  const handleContainerClick = (e: React.MouseEvent) => {
    // ביטול העלמת/הצגת הפקדים אם בוצעה לחיצה כפולה (שמיועדת לזום)
    if (e.detail > 1) return;
    setShowControls(prev => !prev);
  };

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay 
          className="fixed inset-0 z-[140] bg-black/90 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        
        <DialogPrimitive.Content 
          className="fixed inset-0 z-[150] flex flex-col items-center justify-center outline-none"
          aria-describedby={undefined}
        >
          <VisuallyHidden>
            <DialogPrimitive.Title>גלריית תמונות</DialogPrimitive.Title>
          </VisuallyHidden>

          {/* Close Button - Top Right */}
          <div className={cn("absolute top-4 right-4 z-[160] transition-opacity duration-300", showControls ? "opacity-100" : "opacity-0 pointer-events-none")}>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white/80 hover:text-white hover:bg-white/10 rounded-full w-12 h-12 bg-black/20 backdrop-blur-sm"
            >
              <X className="w-6 h-6" />
              <span className="sr-only">סגור</span>
            </Button>
          </div>

          {/* Counter - Top Left */}
          <div className={cn("absolute top-6 left-6 z-[160] transition-opacity duration-300 pointer-events-none", showControls ? "opacity-100" : "opacity-0")}>
            <span className="text-white/90 text-sm font-medium tracking-widest font-mono bg-black/40 px-3 py-1 rounded-full backdrop-blur-sm">
              {selectedIndex + 1} / {images.length}
            </span>
          </div>

          {/* Main Carousel Area */}
          <div 
            className="w-full h-full overflow-hidden flex items-center" 
            ref={emblaRef}
            onClick={handleContainerClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
          >
            <div className="flex h-full w-full touch-pan-y">
              {images.map((src, index) => (
                <div 
                  key={index} 
                  className="flex-[0_0_100%] min-w-0 relative h-full flex items-center justify-center p-0 md:p-10 cursor-auto"
                >
                  <TransformWrapper
                    initialScale={1}
                    minScale={1}
                    maxScale={4}
                    doubleClick={{ mode: "toggle" }}
                    panning={{ velocityDisabled: false, disabled: !isZoomed }} // Disable panning when not zoomed so Embla can swipe!
                    onTransformed={(ref) => {
                      // זיהוי אם התמונה כרגע מוגדלת
                      const currentlyZoomed = ref.state.scale > 1.05;
                      if (currentlyZoomed !== isZoomed) {
                        setIsZoomed(currentlyZoomed);
                      }
                    }}
                  >
                    <TransformComponent 
                      wrapperStyle={{ width: "100%", height: "100%" }} 
                      contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <img
                        src={src}
                        alt={`תמונה ${index + 1}`}
                        className="max-h-[90vh] w-auto max-w-[100vw] md:max-w-[90vw] object-contain select-none shadow-2xl"
                        loading="lazy"
                        draggable={false}
                      />
                    </TransformComponent>
                  </TransformWrapper>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className={cn("block transition-opacity duration-300", (showControls && !isZoomed) ? "opacity-100" : "opacity-0 pointer-events-none")}>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-[160] w-12 h-12 md:w-14 md:h-14 rounded-full text-white/70 hover:text-white hover:bg-white/20 bg-black/20 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none"
              onClick={(e) => { e.stopPropagation(); scrollNext(); }}
            >
              <ChevronRight className="w-8 h-8 md:w-10 md:h-10" />
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-[160] w-12 h-12 md:w-14 md:h-14 rounded-full text-white/70 hover:text-white hover:bg-white/20 bg-black/20 md:bg-transparent backdrop-blur-sm md:backdrop-blur-none"
              onClick={(e) => { e.stopPropagation(); scrollPrev(); }}
            >
              <ChevronLeft className="w-8 h-8 md:w-10 md:h-10" />
            </Button>
          </div>

          {/* Dots Indicator (Bottom) - הוסתר בזמן זום כדי לא להפריע */}
          <div className={cn("absolute bottom-8 left-1/2 -translate-x-1/2 z-[160] flex gap-2 transition-opacity duration-300", (showControls && !isZoomed) ? "opacity-100" : "opacity-0 pointer-events-none")}>
            {images.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.stopPropagation(); emblaApi?.scrollTo(idx); }}
                className={cn(
                  "w-2.5 h-2.5 rounded-full transition-all duration-300 shadow-sm backdrop-blur-sm",
                  idx === selectedIndex 
                    ? "bg-blue-500 scale-110" 
                    : "bg-white/30 hover:bg-white/50"
                )}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>

        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}